import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import { FaHeart, FaRegHeart, FaComment, FaMapMarkerAlt, FaChevronLeft, FaChevronRight, FaCar, FaWalking, FaBicycle, FaTimes, FaCrosshairs } from 'react-icons/fa';
import { likePost, unlikePost, deletePost, isPostLikedByUser } from '../../services/postService';
import { getComments } from '../../services/commentService';
import { useAuth } from '../../contexts/AuthContext';
import Avatar from '../common/Avatar';
import LazyMapDisplay from './LazyMapDisplay';
import CommentList from './CommentList';
import PostOptions from './PostOptions';
import './PostWithMap.css';
import { geocodePlace } from '../../services/mapService';

/**
 * PostWithMap component displays a post with a map in a two-column layout
 * @param {Object} props
 * @param {Object} props.post - Post data object
 * @param {Function} props.onPostUpdate - Callback when post is updated
 * @param {Function} props.onPostDelete - Callback when post is deleted
 * @param {Function} props.onLocationClick - Callback when location is clicked
 * @param {boolean} props.showMapView - Whether to show map view (hide map wrapper)
 */
const PostWithMap = ({ 
  post, 
  onPostUpdate, 
  onPostDelete,
  onLocationClick,
  comments: propComments,
  loadingComments: propLoadingComments,
  onCommentAdded: propOnCommentAdded,
  onCommentDeleted: propOnCommentDeleted,
  onLikeToggle: propOnLikeToggle,
  isLiked: propIsLiked,
  isLiking: propIsLiking,
  showMapView = false,
  feedViewMode = 'list',
  isSelected = false
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  console.log('[PostWithMap] user from useAuth:', user);
  // Guard against invalid element type caused by import mismatches
  const WrapIfValid = (Comp) => (props) => (typeof Comp === 'function' ? <Comp {...props} /> : null);
  const AvatarSafe = WrapIfValid(Avatar);
  const LazyMapDisplaySafe = WrapIfValid(LazyMapDisplay);
  const CommentListSafe = WrapIfValid(CommentList);
  const PostOptionsSafe = WrapIfValid(PostOptions);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllTags, setShowAllTags] = useState(false);
  
  const [isLikedState, setIsLikedState] = useState(false);
  
  // Check if user has liked this post
  useEffect(() => {
    const checkIfLiked = async () => {
      if (!user || !post.id || propIsLiked !== undefined) return;
      try {
        const liked = await isPostLikedByUser(post.id);
        setIsLikedState(liked);
      } catch (error) {
        console.error('Error checking if post is liked:', error);
      }
    };
    
    checkIfLiked();
  }, [user, post.id, propIsLiked]);
  
  const isLiked = propIsLiked !== undefined ? propIsLiked : isLikedState;
  
  const contentTextRef = useRef(null);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isContentOverflowDetected, setIsContentOverflowDetected] = useState(false);
  const [isContentMeasuring, setIsContentMeasuring] = useState(true);
  
  const displayableContent = post?.content || "";
  
  useEffect(() => {
    const fetchPostComments = async () => {
      if (!post.id) return;
      try {
        const fetchedComments = await getComments(post.id);
        setComments(fetchedComments || []);
      } catch (error) {
        console.error(`[PostWithMap ${post.id}] Error fetching initial comments:`, error);
        setComments([]);
      }
    };
    fetchPostComments();
  }, [post.id]);
  
  useEffect(() => {
    setIsContentMeasuring(true);
    setIsContentExpanded(false);
    setIsContentOverflowDetected(false);
  }, [displayableContent]);
  
  useEffect(() => {
    if (isContentMeasuring && contentTextRef.current && post) {
      const element = contentTextRef.current;
      let actualOverflow = false;
      if (displayableContent && displayableContent.trim() !== '') {
        actualOverflow = element.scrollHeight > element.clientHeight;
      }
      setIsContentOverflowDetected(actualOverflow);
      setIsContentMeasuring(false);
    }
  }, [isContentMeasuring, displayableContent, post]);
  
  const locationData = post.place ? {
    id: post.place.id,
    name: post.place.name || post.country || 'Location',
    lat: post.place.lat,
    lng: post.place.lng
  } : null;
  
  const postImages = post.post_images && post.post_images.length > 0 
    ? [...post.post_images].sort((a, b) => a.display_order - b.display_order) 
    : (post.image_url ? [{ url: post.image_url }] : []);
  
  const formattedDate = post.created_at 
    ? formatDistance(new Date(post.created_at), new Date(), { addSuffix: true })
    : 'recently';
  
  const tagList = post.tags ? post.tags.split(',').filter(Boolean) : [];
  const [directions, setDirections] = useState(null);
  const [directionsProfile, setDirectionsProfile] = useState('driving');
  const [directionsInfo, setDirectionsInfo] = useState(null);
  const [bottomSheetExpanded, setBottomSheetExpanded] = useState(false);
  const [geoStatus, setGeoStatus] = useState('idle'); // 'idle' | 'requesting' | 'success' | 'error'
  const [geoError, setGeoError] = useState(null);
  const [geoErrorCode, setGeoErrorCode] = useState(null); // 1: denied, 2: unavailable, 3: timeout
  const [showManualOrigin, setShowManualOrigin] = useState(false);
  const [manualOriginQuery, setManualOriginQuery] = useState('');
  const [isSearchingOrigin, setIsSearchingOrigin] = useState(false);
  const [manualOriginError, setManualOriginError] = useState(null);
  const [savedOrigins, setSavedOrigins] = useState(() => {
    try {
      const raw = localStorage.getItem('savedOrigins');
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  });

  // Debounce profile changes to avoid rapid API calls
  const [effectiveDirectionsProfile, setEffectiveDirectionsProfile] = useState(directionsProfile);
  useEffect(() => {
    const h = setTimeout(() => setEffectiveDirectionsProfile(directionsProfile), 250);
    return () => clearTimeout(h);
  }, [directionsProfile]);

  const requestDirections = () => {
    setBottomSheetExpanded(true);
    setGeoStatus('requesting');
    setGeoError(null);
    setGeoErrorCode(null);

    // If Permissions API is available, we can hint the state to the user
    const doRequest = () => {
      if (!navigator.geolocation) {
        setGeoStatus('error');
        setGeoError('Location is unavailable in this browser or context.');
        setGeoErrorCode(1);
        return;
      }
      navigator.geolocation.getCurrentPosition((pos) => {
        const origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const destination = { lat: locationData.lat, lng: locationData.lng };
        setDirections({ origin, destination });
        setDirectionsProfile('driving');
        setGeoStatus('success');
      }, (err) => {
        // eslint-disable-next-line no-console
        console.warn('[PostWithMap] Geolocation error:', err);
        setGeoStatus('error');
        setGeoErrorCode(err?.code || null);
        if (err?.code === 1) setGeoError('Permission denied. Enable location access to get directions.');
        else if (err?.code === 2) setGeoError('Position unavailable. Try again or check your connection.');
        else if (err?.code === 3) setGeoError('Location request timed out. Try again.');
        else setGeoError('Unable to get your location.');
      }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
    };

    if (navigator.permissions?.query) {
      try {
        navigator.permissions.query({ name: 'geolocation' }).then((status) => {
          // If state is 'denied', calling will fail immediately, but we still call to keep behavior consistent
          doRequest();
        }).catch(() => doRequest());
      } catch (_) {
        doRequest();
      }
    } else {
      doRequest();
    }
  };

  const handleManualOriginSearch = async () => {
    if (!manualOriginQuery.trim()) return;
    setIsSearchingOrigin(true);
    setManualOriginError(null);
    try {
      const results = await geocodePlace(manualOriginQuery.trim());
      if (!results || results.length === 0) {
        setManualOriginError('No results found. Try a different place.');
        return;
      }
      const first = results[0];
      const origin = { lat: first.lat, lng: first.lng };
      const destination = { lat: locationData.lat, lng: locationData.lng };
      setDirections({ origin, destination });
      setDirectionsProfile('driving');
      setGeoStatus('success');
      setShowManualOrigin(false);
      // Save to recent origins (max 2, unique by label)
      try {
        const next = [{ label: manualOriginQuery.trim(), lat: first.lat, lng: first.lng }, ...savedOrigins]
          .filter((v, idx, arr) => idx === arr.findIndex(x => x.label.toLowerCase() === v.label.toLowerCase()))
          .slice(0, 2);
        localStorage.setItem('savedOrigins', JSON.stringify(next));
        setSavedOrigins(next);
      } catch (_) {}
    } catch (e) {
      setManualOriginError('Search failed. Check connection and try again.');
    } finally {
      setIsSearchingOrigin(false);
    }
  };
  
  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === 0 ? postImages.length - 1 : prev - 1
    );
  };
  
  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === postImages.length - 1 ? 0 : prev + 1
    );
  };
  
  const loadComments = async () => {
    if (!post.id) return;
    
    setLoadingComments(true);
    try {
      const fetchedComments = await getComments(post.id);
      setComments(fetchedComments || []);
      setShowComments(true);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };
  
  const handleLikeToggle = async () => {
    if (isLiking || !user) return;
    
    try {
      setIsLiking(true);
      
      // Optimistic UI update
      if (propIsLiked === undefined) {
        setIsLikedState(!isLiked);
      }
      
      const updatedPost = {
        ...post,
        likes_count: isLiked ? Math.max(0, (post.likes_count || 0) - 1) : (post.likes_count || 0) + 1
      };
      
      onPostUpdate(updatedPost);
      
      if (isLiked) {
        await unlikePost(post.id);
      } else {
        await likePost(post.id);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      
      // Revert optimistic update on failure
      if (propIsLiked === undefined) {
        setIsLikedState(isLiked);
      }
      onPostUpdate(post);
      
      // Show user-friendly error message
      if (error.message === 'User has already liked this post') {
        console.log('User already liked this post - no action needed');
      } else if (error.message === 'User has not liked this post') {
        console.log('User has not liked this post - no action needed');
      } else {
        // For other errors, you could show a toast notification here
        console.error('Like operation failed:', error.message);
      }
    } finally {
      setIsLiking(false);
    }
  };
  
  const handleDeleteClick = async () => {
    setConfirmDelete(true);
  };
  
  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await deletePost(post.id);
      onPostDelete(post.id);
    } catch (error) {
      console.error('Error deleting post:', error);
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };
  
  const handleCommentAdded = (newComment) => {
    setComments(prevComments => [...prevComments, newComment]);
    const updatedPost = {
      ...post,
      comments_count: (post.comments_count || 0) + 1
    };
    onPostUpdate(updatedPost);
  };
  
  const handleCommentDeleted = (commentId) => {
    setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
    const updatedPost = {
      ...post,
      comments_count: Math.max((post.comments_count || 0) - 1, 0)
    };
    onPostUpdate(updatedPost);
  };
  
  const handleLocationClick = () => {
    if (locationData && onLocationClick) {
      onLocationClick(locationData);
    }
  };

  // Handle post click to center map on the corresponding pin
  const handlePostClick = (e) => {
    // Don't trigger if clicking on interactive elements
    if (e.target.closest('button') || 
        e.target.closest('a') || 
        e.target.closest('.post-actions') ||
        e.target.closest('.carousel-nav-btn') ||
        e.target.closest('.image-indicators') ||
        e.target.closest('.post-options') ||
        e.target.closest('.comments-section') ||
        e.target.closest('.delete-confirmation')) {
      return;
    }
    
    // Only trigger in map view mode and if we have location data
    if (feedViewMode === 'map' && locationData && onLocationClick) {
      onLocationClick(locationData);
    }
  };
  
  // Helper to get profile link by user_id or username
  const getProfileLink = (user) => {
    if (user?.username) return `/profile/${user.username}`;
    if (user?.user_id) return `/profile/${user.user_id}`;
    return '/profile';
  };
  
  // Use props if provided (for PostDetail), otherwise use internal state (for Feed)
  const commentsToUse = propComments !== undefined ? propComments : comments;
  const loadingCommentsToUse = propLoadingComments !== undefined ? propLoadingComments : loadingComments;
  const handleCommentAddedToUse = propOnCommentAdded || handleCommentAdded;
  const handleCommentDeletedToUse = propOnCommentDeleted || handleCommentDeleted;
  const handleLikeToggleToUse = propOnLikeToggle || handleLikeToggle;
  const isLikedToUse = propIsLiked !== undefined ? propIsLiked : isLiked;
  const isLikingToUse = propIsLiking !== undefined ? propIsLiking : isLiking;
  
  console.log('[PostWithMap] Like button disabled:', isLikingToUse, !user, user);
  // Debug: log the comment count prop to verify updates
  console.log('[PostWithMap] post.comments_count:', post.comments_count);
  console.log('[PostWithMap] post.likes_count:', post.likes_count);
  
  return (
    <div 
      className={`post-with-map ${isDeleting ? 'post-loading' : ''} ${isSelected ? 'selected-post' : ''}`}
      onClick={handlePostClick}
      style={{ cursor: feedViewMode === 'map' && locationData ? 'pointer' : 'default' }}
    >
      <div className="post-content-column">
        {/* Post header: avatar, username, date (left-aligned) */}
        <div className="post-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to={getProfileLink(post.user)} className="post-user-info">
            <AvatarSafe 
              src={post.user?.profilePicture} 
              alt={post.user?.username || 'User'} 
              username={post.user?.username}
              className="post-avatar"
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <h3 className="post-username" style={{ margin: 0 }}>{post.user?.username || 'Unknown User'}</h3>
              <p className="post-timestamp" style={{ margin: '2px 0 0 0', fontSize: '0.95em', color: '#888' }}>{formattedDate}</p>
            </div>
          </Link>
          {user?.id === post.user_id && (
            <PostOptionsSafe 
              onDelete={handleDeleteClick} 
              disabled={isDeleting}
            />
          )}
        </div>
        
        {postImages.length > 0 && (
          <div className="post-images-carousel">
            <div className="post-image-container">
              <img 
                src={postImages[currentImageIndex].url} 
                alt={post.title || `Image ${currentImageIndex + 1}`} 
                className="post-image" 
                loading="lazy"
              />
              
              {postImages.length > 1 && (
                <>
                  <button 
                    className="carousel-nav-btn carousel-prev-btn" 
                    onClick={handlePrevImage}
                    aria-label="Previous image"
                  >
                    <FaChevronLeft />
                  </button>
                  <button 
                    className="carousel-nav-btn carousel-next-btn" 
                    onClick={handleNextImage}
                    aria-label="Next image"
                  >
                    <FaChevronRight />
                  </button>
                  
                  <div className="image-indicators">
                    {postImages.map((_, index) => (
                      <span 
                        key={index} 
                        className={`indicator-dot ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(index);
                        }}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Post title below the image */}
        {post.title && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', margin: '0.0rem 0 0.0rem 0' }}>
            <span className="post-title" style={{ fontWeight: 700, fontSize: '1.15em', color: '#222', letterSpacing: '0.01em' }}>{post.title}</span>
          </div>
        )}
        
        
        
        {post.content && (
          <div className="post-text-wrapper">
            <div
              ref={contentTextRef}
              className={`post-text-content ${
                isContentMeasuring || (!isContentExpanded && isContentOverflowDetected) ? 'apply-clamp-post-with-map' : ''
              }`}
            >
              {displayableContent}
            </div>
            {!isContentMeasuring && isContentOverflowDetected && (
              <button
                onClick={() => setIsContentExpanded(prev => !prev)}
                className="show-more-less-button"
                type="button"
              >
                {isContentExpanded ? 'Show Less' : 'Show More'}
              </button>
            )}
          </div>
        )}
        
        <div className="post-actions">
          <button 
            className={`post-action-button ${isLikedToUse ? 'liked' : ''}`}
            onClick={handleLikeToggleToUse}
            disabled={isLikingToUse || !user}
            aria-label={isLikedToUse ? 'Unlike post' : 'Like post'}
          >
            {isLikedToUse ? <FaHeart /> : <FaRegHeart />}
            <span className="post-action-count">
              {post.likes_count || 0}
            </span>
          </button>
          
          <button 
            className={`post-action-button ${showComments ? 'active' : ''}`}
            onClick={() => {
              if (!showComments) {
                loadComments();
              } else {
                setShowComments(false);
              }
            }}
            aria-label={showComments ? 'Hide comments' : 'Show comments'}
          >
            <FaComment />
            <span className="post-action-count">
              {post.comments_count || 0}
            </span>
          </button>
          {/* Tags to the right of the comment icon */}
          <div className="post-tags-actions" style={{ position: 'relative', display: 'inline-block', marginLeft: '8px' }}>
            {tagList.length > 0 && (
              <>
                {tagList.slice(0, 2).map((tag, idx) => (
                  <span key={idx} className="post-tag">{tag.trim()}</span>
                ))}
                {tagList.length > 2 && (
                  <button
                    className="post-tags-ellipsis"
                    onClick={() => setShowAllTags(v => !v)}
                    aria-label="Show all tags"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', padding: '0 4px' }}
                  >
                    ...
                  </button>
                )}
                {showAllTags && (
                  <div className="post-tags-dropdown" style={{ position: 'absolute', top: '100%', left: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', zIndex: 10, minWidth: '120px', padding: '8px' }}>
                    {tagList.map((tag, idx) => (
                      <div key={idx} className="post-tag" style={{ display: 'block', margin: '2px 0' }}>{tag.trim()}</div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {showComments && (
          <div className="comments-section">
            <CommentListSafe 
              comments={commentsToUse} 
              postId={post.id}
              currentUser={user}
              onCommentAdded={handleCommentAddedToUse}
              onCommentDeleted={handleCommentDeletedToUse}
              loading={loadingCommentsToUse}
              expanded={true}
            />
          </div>
        )}
        
        {confirmDelete && (
          <div className="delete-confirmation">
            <p>Are you sure you want to delete this post?</p>
            <div className="delete-buttons">
              <button 
                className="confirm-delete-button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <button 
                className="cancel-delete-button"
                onClick={() => setConfirmDelete(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      
      {locationData && feedViewMode === 'list' && (
        <div className="post-map-wrapper" style={{ position: 'relative' }}>
          <div className="feed-post-map-column">
            <div className="post-map">
              <LazyMapDisplaySafe 
                location={locationData}
                bottomSheetEnabled={true}
                bottomSheetExpanded={bottomSheetExpanded}
                onBottomSheetToggle={() => setBottomSheetExpanded(!bottomSheetExpanded)}
                onBottomSheetCollapse={() => setBottomSheetExpanded(false)}
                bottomSheetCollapsedHeight={80}
                bottomSheetExpandedHeight={320}
                bottomSheetTitle={locationData.name}
                bottomSheetSubtitle={`${post.place?.region || ''}${post.place?.region ? ', ' : ''}${post.place?.country || ''}`}
                onBottomSheetPrimary={requestDirections}
                bottomSheetPrimaryLabel="Directions"
                directions={directions}
                directionsProfile={effectiveDirectionsProfile}
                onDirectionsInfo={setDirectionsInfo}
                bottomSheetContent={(
                  <div>
                    {/* Profile switcher and ETA */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {['driving','walking','cycling'].map((p) => {
                          const Icon = p === 'driving' ? FaCar : p === 'walking' ? FaWalking : FaBicycle;
                          return (
                            <button
                              key={p}
                              aria-pressed={directionsProfile === p}
                              title={p.charAt(0).toUpperCase() + p.slice(1)}
                              onClick={() => {
                                setDirectionsProfile(p);
                              }}
                              style={{
                                background: directionsProfile === p ? '#2b69f8' : '#f4f6fa',
                                color: directionsProfile === p ? '#fff' : '#333',
                                border: '1px solid #e6eaf2',
                                borderRadius: '10px',
                                padding: '6px 10px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6
                              }}
                            >
                              <Icon />
                              <span style={{ display: 'none' }}>{p}</span>
                            </button>
                          );
                        })}
                      </div>
                      {directionsInfo && (
                        <div style={{ color: '#555', fontSize: '12px' }}>
                          {(() => {
                            const km = (directionsInfo.distanceMeters || 0) / 1000;
                            const mins = Math.round((directionsInfo.durationSeconds || 0) / 60);
                            return `${km.toFixed(1)} km • ${mins} min`;
                          })()}
                        </div>
                      )}
                    </div>
                    {geoStatus === 'requesting' && (
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        Getting your location…
                      </div>
                    )}
                    {geoStatus === 'error' && (
                      <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                        <div style={{ color: '#b00020' }}>
                          {geoError || 'Unable to get your location.'}
                        </div>
                        {geoErrorCode === 1 && (
                          <div style={{ marginTop: '6px', color: '#666' }}>
                            On some browsers you may see an info icon instead of a lock. Enable Location for this site, then Retry.
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          <button
                            onClick={requestDirections}
                            style={{ background: '#2b69f8', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                          >
                            Retry
                          </button>
                          <button
                            onClick={() => setShowManualOrigin((v) => !v)}
                            style={{ background: '#f4f6fa', border: '1px solid #e6eaf2', color: '#333', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                          >
                            {showManualOrigin ? 'Hide manual start' : 'Use manual start'}
                          </button>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(locationData.name)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#2b69f8', textDecoration: 'none', fontWeight: 600, alignSelf: 'center' }}
                          >
                            Open in Google Maps
                          </a>
                        </div>
                        {showManualOrigin && (
                          <div style={{ marginTop: 8 }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <input
                                type="text"
                                value={manualOriginQuery}
                                onChange={(e) => setManualOriginQuery(e.target.value)}
                                placeholder="Enter your start location"
                                style={{ flex: 1, padding: '6px 8px', border: '1px solid #e6eaf2', borderRadius: 8, fontSize: 12 }}
                              />
                              <button
                                onClick={handleManualOriginSearch}
                                disabled={isSearchingOrigin}
                                style={{ background: '#2b69f8', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                              >
                                {isSearchingOrigin ? 'Searching…' : 'Set start'}
                              </button>
                            </div>
                            {manualOriginError && (
                              <div style={{ color: '#b00020', marginTop: 6 }}>{manualOriginError}</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {post.content && (
                      <p style={{ margin: '4px 0 8px', color: '#444', lineHeight: 1.35 }}>
                        {post.content}
                      </p>
                    )}
                    <div className="bottom-docked-sheet__actions">
                      {!!directions && (
                        <button
                          onClick={() => {
                            setDirections(null);
                            setDirectionsInfo(null);
                            setGeoStatus('idle');
                            setBottomSheetExpanded(false);
                          }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <FaTimes /> Clear route
                        </button>
                      )}
                      <button onClick={() => {/* Save/Bookmark placeholder */}}>Save</button>
                      <button onClick={() => {
                        const shareData = {
                          title: post.title || locationData.name,
                          text: `Check this place: ${locationData.name}`,
                          url: `${window.location.origin}/post/${post.id}`
                        };
                        if (navigator.share) {
                          navigator.share(shareData).catch(() => {});
                        } else {
                          navigator.clipboard?.writeText(shareData.url);
                        }
                      }}>Share</button>
                      <button onClick={() => {/* Check-in placeholder */}}>Check-in</button>
                      <button onClick={() => {/* Add Note placeholder */}}>Add Note</button>
                    </div>
                    {postImages.length > 0 && (
                      <div className="bottom-docked-sheet__photos">
                        {postImages.slice(0, 8).map((img, idx) => (
                          <img key={idx} src={img.url} alt="" loading="lazy" />
                        ))}
                      </div>
                    )}

                    {/* Saved/Recent origins chips under manual start */}
                    {showManualOrigin && savedOrigins.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ color: '#666', fontSize: 12, marginBottom: 6 }}>Recent starts</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {savedOrigins.map((o, i) => (
                            <button
                              key={i}
                              onClick={() => {
                                const origin = { lat: o.lat, lng: o.lng };
                                const destination = { lat: locationData.lat, lng: locationData.lng };
                                setDirections({ origin, destination });
                                setDirectionsProfile('driving');
                                setGeoStatus('success');
                                setShowManualOrigin(false);
                              }}
                              style={{ background: '#f4f6fa', border: '1px solid #e6eaf2', color: '#333', borderRadius: 16, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              />
              <div className="post-map-location-label" onClick={handleLocationClick}>
                <FaMapMarkerAlt />
                <span>{locationData.name}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="dimensional-orbs">
        <div className="dimensional-orb orb-1"></div>
        <div className="dimensional-orb orb-2"></div>
        <div className="dimensional-orb orb-3"></div>
      </div>
    </div>
  );
};

export default PostWithMap; 
